import { LoadError } from "@dehub/theme";
import { Box, Button, Popover, Text } from "@mantine/core";
import {
  CalendarBlankIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  DownloadSimpleIcon,
  FilePdfIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSWRConfig } from "swr";
import { IPeriodBalanceRow } from "../../../apis/ledger";
import { Page, PageHeader } from "../../../components/layout/Page";
import { Notice } from "../../../components/ui/notice";
import { currencyFormat } from "../../../utils";
import { ReportPeriod } from "./report-period";
import classes from "./ReportShell.module.css";

export type { ReportPeriod };

type SectionRow<T> = { kind: "section"; section: string } | { kind: "line"; row: T };

/**
 * Balance sheet and income statement lines arrive pre-sorted by `sortOrder`
 * (`groupByLines` sorts before grouping), which already clusters them by
 * `section` — this just turns that clustering into an explicit header row
 * every time the section changes.
 */
export const withSectionHeaders = <T extends { section: string }>(rows: T[]): SectionRow<T>[] => {
  const result: SectionRow<T>[] = [];
  let last: string | null = null;
  for (const row of rows) {
    if (row.section !== last) {
      result.push({ kind: "section", section: row.section });
      last = row.section;
    }
    result.push({ kind: "line", row });
  }
  return result;
};

/** «Тэнцвэртэй» ногоон / «Тэнцээгүй N» улаан pill — тайлан бүрийн шалгалт. */
export const StatusPill = ({ ok, children }: { ok: boolean; children: ReactNode }) => (
  <Text span className={`${classes.pill} ${ok ? classes.pillOk : classes.pillBad}`}>
    {ok ? <CheckCircleIcon size={13} weight="bold" /> : <WarningCircleIcon size={13} weight="bold" />}
    {children}
  </Text>
);

/** Шар/улаан анхааруулгын мөр: гарчиг + тайлбар + холбоос (нийтлэг `Notice`). */
export const NoticeBar = ({
  tone = "warn",
  title,
  text,
  linkLabel,
  linkTo,
}: {
  tone?: "warn" | "danger";
  title: string;
  text?: ReactNode;
  linkLabel?: string;
  linkTo?: string;
}) => (
  <Notice tone={tone === "danger" ? "danger" : "warning"} title={title}>
    {text}
    {linkLabel && linkTo && (
      <>
        {text ? " " : null}
        <Link to={linkTo} className={classes.noticeLink}>{linkLabel}</Link>
      </>
    )}
  </Notice>
);

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/** «2026 · 9-р сар» — нэг товч, дарахад он/сар сонгогч. */
export const PeriodChip = ({ period, onChange }: { period: ReportPeriod; onChange: (p: ReportPeriod) => void }) => {
  const { t } = useTranslation("billing");
  const [opened, setOpened] = useState(false);
  const [year, setYear] = useState(period.year);

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" shadow="md" withinPortal>
      <Popover.Target>
        <Box
          component="button"
          type="button"
          className={classes.periodBtn}
          onClick={() => {
            setYear(period.year);
            setOpened((o) => !o);
          }}
        >
          <CalendarBlankIcon size={15} />
          {period.year} · {t(`ledger.report.month.${period.month}`)}
        </Box>
      </Popover.Target>
      <Popover.Dropdown>
        <Box className={classes.periodPanel}>
          <Box className={classes.yearRow}>
            <Button variant="subtle" color="gray" size="compact-sm" onClick={() => setYear((y) => y - 1)} aria-label={t("ledger.report.periodYear")}>
              <CaretLeftIcon size={14} weight="bold" />
            </Button>
            <Text span>{year}</Text>
            <Button variant="subtle" color="gray" size="compact-sm" onClick={() => setYear((y) => y + 1)} aria-label={t("ledger.report.periodYear")}>
              <CaretRightIcon size={14} weight="bold" />
            </Button>
          </Box>
          <Box className={classes.monthGrid}>
            {MONTHS.map((m) => (
              <Box
                key={m}
                component="button"
                type="button"
                className={classes.monthBtn}
                data-active={year === period.year && m === period.month ? "" : undefined}
                onClick={() => {
                  onChange({ year, month: m });
                  setOpened(false);
                }}
              >
                {t(`ledger.report.month.${m}`)}
              </Box>
            ))}
          </Box>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};

export interface ReportExportConfig {
  onExcel: () => void;
  /** Omit for reports the backend does not render to PDF (trial balance, aging, statement). */
  onPdf?: () => void;
  excelLoading?: boolean;
  pdfLoading?: boolean;
}

export const ExportButtons = ({ onExport }: { onExport: ReportExportConfig }) => {
  const { t } = useTranslation("billing");
  return (
    <>
      <Button variant="default" leftSection={<DownloadSimpleIcon size={14} />} loading={onExport.excelLoading} onClick={onExport.onExcel}>
        {t("ledger.report.exportExcel")}
      </Button>
      {onExport.onPdf && (
        <Button variant="default" leftSection={<FilePdfIcon size={14} />} loading={onExport.pdfLoading} onClick={onExport.onPdf}>
          {t("ledger.report.exportPdf")}
        </Button>
      )}
    </>
  );
};

interface ReportShellProps {
  title: string;
  /** Толгойн дэргэх төлвийн pill (StatusPill). */
  status?: ReactNode;
  subtitle?: ReactNode;
  period: ReportPeriod;
  onPeriodChange: (period: ReportPeriod) => void;
  onExport?: ReportExportConfig;
  /** Хугацааны chip-ийн өмнөх нэмэлт удирдлага (жишээ нь «Тэг дансыг нуух»). */
  controls?: ReactNode;
  /** Толгойн доорх анхааруулгын мөр. */
  banner?: ReactNode;
  /**
   * The `useSwr` fetch error, if any. A failed fetch leaves `data` undefined,
   * which would otherwise render exactly like a period with zero activity —
   * on a financial report those are different answers.
   */
  error?: unknown;
  children: ReactNode;
}

const errorDetail = (error: unknown): string => {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.length > 0) return message;
  }
  return "";
};

/**
 * Тайлангийн нийтлэг бүрхүүл: гарчиг + төлвийн pill, хугацааны chip,
 * экспортын товчнууд, анхааруулгын мөр. Хугацаа үргэлж харагдана.
 */
export const ReportShell = ({
  title,
  status,
  subtitle,
  period,
  onPeriodChange,
  onExport,
  controls,
  banner,
  error,
  children,
}: ReportShellProps) => {
  const { t } = useTranslation(["billing", "common"]);
  const detail = errorDetail(error);
  const { mutate: globalMutate } = useSWRConfig();

  return (
    <Page>
      <PageHeader
        title={
          <Box component="span" className={classes.titleRow}>
            {title}
            {status}
          </Box>
        }
        subtitle={subtitle}
        extra={
          <Box className={classes.headerRight}>
            {controls}
            <PeriodChip period={period} onChange={onPeriodChange} />
            {onExport && <ExportButtons onExport={onExport} />}
          </Box>
        }
      />

      {banner}

      {error ? (
        <LoadError
          title={t("billing:ledger.report.loadError")}
          message={detail || undefined}
          onRetry={() => {
            void globalMutate(() => true);
          }}
        />
      ) : (
        children
      )}
    </Page>
  );
};

/**
 * Balance sheet and income statement both carry an `unmapped` array — accounts
 * nobody assigned to a report line, and therefore missing from the totals.
 */
export const UnmappedSection = ({ rows }: { rows: IPeriodBalanceRow[] }) => {
  const { t } = useTranslation("billing");
  if (rows.length === 0) return null;

  return (
    <NoticeBar
      title={t("ledger.report.unmappedTitle", { count: rows.length })}
      text={
        <>
          {rows
            .map((r) => {
              const base = `${r.code} ${r.name}`;
              return Number(r.closingBalance) !== 0 ? `${base} (${currencyFormat(r.closingBalance)})` : base;
            })
            .join(", ")}
          {" - "}
          {t("ledger.report.unmappedHint")}
        </>
      }
    />
  );
};

export default ReportShell;
